// src/pages/AgenticWorkflow/components/DropdownMenu/DropdownMenu.tsx
import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import type { Workflow } from '../../AgenticWorkflow.types';
import styles from './DropdownMenu.module.scss';
import { Button, Dropdown } from 'common/components';
import { toast } from 'react-toastify';
import NotificationModal from 'components/NotificationModal';
import { useDeleteWorkflow } from 'services/hooks/mutations';

export interface DropdownMenuProps {
    workflow: Workflow;
    disabled?: boolean;
    onDeleteSuccess?: () => void;
    onUploadClick?: (workflow: Workflow) => void;
}

export const DropdownMenu: React.FC<DropdownMenuProps> = ({
    workflow,
    disabled = false,
    onDeleteSuccess,
    onUploadClick,
}) => {
    const [isOpen, setIsOpen] = useState(false);
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
    const buttonRef = useRef<HTMLButtonElement | null>(null);
    const navigate = useNavigate();

    const { deleteWorkflow, isPending } = useDeleteWorkflow();

    const toggleDropdown = (e: React.MouseEvent): void => {
        e.stopPropagation();
        if (disabled) return;
        setIsOpen((prevOpen) => !prevOpen);
    };

    const handleEdit = (e: React.MouseEvent): void => {
        if (disabled) return;
        e.stopPropagation();
        navigate(`/agentic-workflow-builder/edit/${workflow.id}`, {
            state: { workflow },
        });
        setIsOpen(false);
    };

    const handleUpload = (e: React.MouseEvent): void => {
        if (disabled) return;
        e.stopPropagation();
        if (onUploadClick) {
            onUploadClick(workflow);
        }
        setIsOpen(false);
    };

    const handleDelete = (): void => {
        if (disabled) return;
        setIsDeleteModalOpen(true);
    };

    const handleDeleteConfirm = async (): Promise<void> => {
        await deleteWorkflow(workflow.id, {
            onSuccess: () => {
                if (onDeleteSuccess) {
                    onDeleteSuccess();
                }
                toast.success('Workflow deleted successfully!');
            },
            onError: (error) => {
                console.error('Error deleting workflow:', error);
                toast.error('Failed to delete workflow.');
            },
            onSettled: () => {
                setIsDeleteModalOpen(false);
                setIsOpen(false);
            },
        });
    };

    const handleCloseDeleteModal = (): void => {
        if (isPending) return;
        setIsDeleteModalOpen(false);
        setIsOpen(false);
    };

    const handleDuplicate = (): void => {
        if (disabled) return;
        navigate(`/agentic-workflow-builder/edit/${workflow.id}`, {
            state: { workflow, duplicate: true },
        });
    };

    return (
        <>
            <div className={styles.dropdown}>
                <Button ref={buttonRef} onClick={toggleDropdown} disabled={disabled} variant='primary' size='sm'>
                    Actions
                </Button>

                <Dropdown isOpen={isOpen} onClose={() => setIsOpen(false)} toggleRef={buttonRef}>
                    <div className={styles.menuItem} onClick={handleEdit}>
                        Edit
                    </div>
                    <div className={styles.menuItem} onClick={handleDuplicate}>
                        Duplicate
                    </div>
                    <div className={styles.menuItem} onClick={handleUpload}>
                        Upload Leads
                    </div>
                    <div className={`${styles.menuItem} ${styles.danger}`} onClick={handleDelete}>
                        Delete
                    </div>
                </Dropdown>
            </div>

            <NotificationModal
                isOpen={isDeleteModalOpen}
                onClose={handleCloseDeleteModal}
                title='Delete Workflow'
                type='error'
                message='Are you sure you want to delete this workflow?'
                buttons={[
                    {
                        label: 'Cancel',
                        onClick: handleCloseDeleteModal,
                        variant: 'secondary',
                    },
                    { label: 'Delete', onClick: handleDeleteConfirm, variant: 'danger', isLoading: isPending },
                ]}
            />
        </>
    );
};

export default DropdownMenu;
